/**
 * Llama and trader llama (vanilla): health 15-30, strength 1-5, speed 0.175,
 * four coats (creamy, white, brown, gray), 0.9 x 1.87.
 *  - Neutral: hit by the player it spits (1 damage, spit.ts) instead of fleeing,
 *    and untamed adults spit at wolves that come near. Babies flee.
 *  - Taming has no rider here: an empty-hand click is one "mount attempt" - the
 *    llama is tamed if rand(30) < temper, else it bucks (temper +5). Wheat
 *    (heals 2, temper +3) and hay bales (heals 10, temper +6, breed a tame llama)
 *    feed it. A tamed adult takes a chest: 3 x strength slots (opened through the
 *    llama screen), dropped with the chest block on death. A llama cannot be
 *    steered; lead it with a lead instead.
 *  - Caravans: a llama a player leads pulls other loose llamas within 9 blocks
 *    into a line behind it (TraderSystem sets `caravanPrev`).
 *  - Trader llamas wear the trader decor, are leashed to their trader, follow it,
 *    defend it (spit at whoever hit it, and at zombies / illagers that come near)
 *    and despawn with it (or after 47999 ticks of their own).
 * Drops: 0-2 leather (+ chest and its contents); XP 1-3.
 *
 * `view.color`: bits 0-1 coat, bits 2-6 carpet (0 none, 1-16 dye + 1), bit 7 chest.
 */

import { BLOCKS, CHEST_FACING_0, HAY_BALE_ID } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Animal, LOVE_TICKS } from '../animals';
import type { EntityHost } from '../host';
import { leashes } from '../leash';
import type { Mob, MobCtx } from '../mob';
import { playSound, traderByUid, traderHooks, vanishedTraders } from './hooks';
import { spits } from './spit';
import type { WanderingTrader } from './trader';

export const LLAMA_VARIANTS = ['creamy', 'white', 'brown', 'gray'] as const;
export const LLAMA_MAX_TEMPER = 30;
export const LLAMA_SPEED = 0.175;
export const TRADER_LLAMA_DESPAWN = 47999;
/** Spit range (RangedAttackGoal 20) and the pause between shots. */
export const SPIT_RANGE = 20;
export const SPIT_COOLDOWN = 40;
/** Followers a leading llama pulls into its caravan. */
export const CARAVAN_MAX = 9;
export const CARAVAN_RANGE = 9;

export const chestSlots = (strength: number): number => 3 * strength;
/** Vanilla AbstractHorse health: 15 + d8 + d9. */
export const rollLlamaHealth = (rnd: () => number): number => 15 + Math.floor(rnd() * 8) + Math.floor(rnd() * 9);
export const rollStrength = (rnd: () => number): number => 1 + Math.floor(rnd() * 5);
export const isChestItem = (id: number): boolean => id > 0 && id < 1000 && BLOCKS[id]?.name === 'Chest';
export const isLlamaFood = (id: number): boolean => id === I.WHEAT || id === HAY_BALE_ID;

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));
type Target = Mob | 'player' | null;

export type LlamaClick = 'none' | 'handled' | 'consumed' | 'open';

export class Llama extends Animal {
  protected raw = 0;
  protected cooked = 0;
  variant: number;
  strength: number;
  tame = false;
  temper = 0;
  chest = false;
  /** Chest contents, `chestSlots(strength)` entries (empty stacks have count 0). */
  items: Stack[] = [];
  /** Carpet worn: 0 none, 1-16 = dye index + 1 (no carpet block exists in this build, so only saves / /summon can set it). */
  carpet = 0;
  /** Trader llamas: the despawn countdown and the owning trader's uid. */
  despawn = 0;
  traderUid = 0;
  /** Set after a load: the system re-ties it to its trader once. */
  needsLeash = false;
  /** Caravan: the llama in front of this one. */
  caravanPrev: Llama | null = null;

  private target: Target = null;
  private targetTicks = 0;
  private spitCd = 20;
  private scanCd: number;
  private traderRef: (Mob & { uid: number }) | null = null;
  readonly isTrader: boolean;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number, type: 'llama' | 'trader_llama' = 'llama') {
    super(id, type as unknown as 'pig', x, y, z, { width: 0.9, height: 1.87, eye: 1.7, health: 15, speed: LLAMA_SPEED }, rnd);
    this.isTrader = type === 'trader_llama';
    this.variant = Math.floor(rnd() * 4);
    this.strength = rollStrength(rnd);
    this.setMaxHealth(rollLlamaHealth(rnd));
    this.resizeChest();
    this.scanCd = Math.floor(rnd() * 10);
    if (this.isTrader) this.despawn = TRADER_LLAMA_DESPAWN;
    this.panicMod = 1.2;
  }

  setMaxHealth(h: number): void {
    (this as unknown as { maxHealth: number }).maxHealth = h;
    this.health = h;
  }
  setVariant(v: number): void { this.variant = Math.max(0, Math.min(3, Math.floor(v))); }
  setStrength(s: number): void { this.strength = Math.max(1, Math.min(5, Math.floor(s))); this.resizeChest(); }

  private resizeChest(): void {
    const n = chestSlots(this.strength);
    while (this.items.length < n) this.items.push({ id: 0, count: 0, dmg: 0 });
    this.items.length = n;
  }

  override get canFallInLove(): boolean { return this.tame && this.health >= this.maxHealth && super.canFallInLove; }

  // ------------------------------------------------------------ player use

  /**
   * Right-click with `held` (0 = empty hand). 'consumed' = one of the held item
   * was used up, 'open' = show the chest screen, 'handled' = something happened.
   */
  rightClick(held: number, sneaking: boolean, rnd: () => number): LlamaClick {
    if (!this.alive) return 'none';
    if (held === I.WHEAT || held === HAY_BALE_ID) return this.feed(held, rnd) ? 'consumed' : 'none';
    if (this.tame && !this.baby && !this.chest && isChestItem(held)) { this.chest = true; this.resizeChest(); return 'consumed'; }
    if (this.tame && this.chest && sneaking) return 'open';
    if (!this.tame && !this.baby && held === 0) { this.mountAttempt(rnd); return 'handled'; }
    return 'none';
  }

  /** Vanilla handleEating: wheat heals 2 / temper +3, hay bale heals 10 / temper +6 (and love for a tame adult). */
  feed(item: number, rnd: () => number): boolean {
    const hay = item === HAY_BALE_ID;
    if (!hay && item !== I.WHEAT) return false;
    let used = false;
    const heal = hay ? 10 : 2;
    if (this.health < this.maxHealth) { this.health = Math.min(this.maxHealth, this.health + heal); used = true; }
    if (this.baby) {
      this.growTicks = Math.max(1, this.growTicks - Math.floor(this.growTicks * (hay ? 0.2 : 0.1)));
      used = true;
    } else if (!this.tame && this.temper < LLAMA_MAX_TEMPER) {
      this.temper = Math.min(LLAMA_MAX_TEMPER, this.temper + (hay ? 6 : 3));
      used = true;
      if (rnd() < 0.3) this.host?.effect('poof', this.x, this.y + this.height, this.z);
    }
    if (hay && this.tame && !this.baby && this.canFallInLove) { this.loveTicks = LOVE_TICKS; used = true; }
    if (used) this.host?.effect(this.tame ? 'heart' : 'poof', this.x, this.y + this.height, this.z);
    return used;
  }

  /** One empty-hand ride attempt: tamed with chance temper / 30, else it bucks and gets 5 more temper. True = tamed. */
  mountAttempt(rnd: () => number): boolean {
    if (Math.floor(rnd() * LLAMA_MAX_TEMPER) < this.temper) {
      this.tame = true; this.despawn = 0;
      for (let i = 0; i < 7; i++) this.host?.effect('heart', this.x + (rnd() - 0.5), this.y + 1 + rnd(), this.z + (rnd() - 0.5));
      return true;
    }
    this.temper = Math.min(LLAMA_MAX_TEMPER, this.temper + 5);
    for (let i = 0; i < 7; i++) this.host?.effect('poof', this.x + (rnd() - 0.5), this.y + 1 + rnd(), this.z + (rnd() - 0.5));
    this.vy = 0.25;
    return false;
  }

  // ---------------------------------------------------------------- combat

  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    if (this.baby) { super.onHurtBy(fromX, fromZ, rnd); return; }
    // neutral: spit back at the player (a tame llama only flinches)
    if (!this.tame) { this.target = 'player'; this.targetTicks = 300; }
  }
  /** Hit by another mob (spit, zombie, wolf): target it. */
  onHurtByMob(attacker: Mob): void { if (!this.baby && attacker !== (this as unknown as Mob)) { this.target = attacker; this.targetTicks = 300; } }

  private tPos(ctx: MobCtx, t: Exclude<Target, null>): { x: number; y: number; z: number; mid: number } {
    if (t === 'player') { const p = ctx.host.player; return { x: p.x, y: p.y, z: p.z, mid: p.y + p.height * 0.33 }; }
    return { x: t.x, y: t.y, z: t.z, mid: t.y + t.height * 0.33 };
  }

  private findTarget(trader: (Mob & { uid: number }) | null): void {
    if (this.baby) return;
    let best: Mob | null = null, bd = Infinity;
    const consider = (m: Mob, r: number): void => {
      const d = (m.x - this.x) ** 2 + (m.z - this.z) ** 2;
      if (d < r * r && d < bd) { bd = d; best = m; }
    };
    for (const m of traderHooks.mobs()) {
      if (!m.alive || m === (this as Mob)) continue;
      if (m.type === 'wolf' && !this.tame) consider(m, 10);
      else if (this.isTrader && (m.type === 'zombie' || m.type === 'zombie_villager' || m.type === 'husk' || m.type === 'drowned' || m.type === 'vindicator' || m.type === 'pillager')) consider(m, 9);
    }
    const t = trader as unknown as WanderingTrader | null;
    if (t && this.isTrader) {
      if (t.hurtByPlayer > 0) { this.target = 'player'; this.targetTicks = 160; return; }
      if (t.attacker && t.attacker.alive && t.attacker.type !== 'trader_llama') { this.target = t.attacker; this.targetTicks = 160; return; }
    }
    if (best) { this.target = best; this.targetTicks = 160; }
  }

  private tryTarget(ctx: MobCtx): boolean {
    const t = this.target;
    if (t === null) return false;
    if (--this.targetTicks <= 0 || (t === 'player' ? !ctx.targetable() : !t.alive)) { this.target = null; return false; }
    const q = this.tPos(ctx, t);
    const dx = q.x - this.x, dz = q.z - this.z, d = Math.hypot(dx, dz);
    if (d > SPIT_RANGE + 6) { this.target = null; return false; }
    this.faceToward(q.x, q.z);
    this.lookAt(q.x, q.mid, q.z);
    const sees = ctx.los(this.x, this.eyeY, this.z, q.x, q.mid, q.z);
    if (!sees || d > 12) {
      if (!this.navTo(ctx, q.x, q.y, q.z, 1.25, 3, true, 24, 200)) this.setMove(dx, dz, 1.25);
    }
    if (this.spitCd > 0) this.spitCd--;
    if (this.spitCd <= 0 && sees && d <= SPIT_RANGE) {
      this.spitCd = SPIT_COOLDOWN + Math.floor(ctx.host.rnd() * 20);
      this.spitAt(ctx, q.x, q.mid, q.z);
    }
    return true;
  }

  /** Vanilla performRangedAttack: aim at the target's lower body with a lob of 0.2 x range. */
  spitAt(ctx: MobCtx, tx: number, ty: number, tz: number): void {
    const fx = -Math.sin(this.headYaw), fz = -Math.cos(this.headYaw);
    const sx = this.x + fx * 0.9, sy = this.y + this.height * 0.85, sz = this.z + fz * 0.9;
    const dx = tx - sx, dz = tz - sz, h = Math.hypot(dx, dz);
    spits.fire(this, sx, sy, sz, dx, ty - sy + h * 0.2, dz, ctx.host.rnd);
    this.swing();
    playSound('entity.llama.spit', this);
  }

  // -------------------------------------------------------------------- ai

  private vanish(): void {
    leashes.detach(this, false);
    this.removed = true;
    this.host?.effect('poof', this.x, this.y + this.height / 2, this.z);
  }

  protected override ai(ctx: MobCtx): void {
    if (this.isTrader) {
      if (this.traderUid && vanishedTraders.has(this.traderUid)) { this.vanish(); return; }
      if (this.phase(ctx, 20)) this.traderRef = traderByUid(this.traderUid);
      const holder = leashes.holderOf(this);
      if (!holder || holder.kind === 'mob') {
        const t = this.traderRef as unknown as WanderingTrader | null;
        // leashed to its trader it despawns with it; loose it counts its own 47999 ticks
        if (holder && t) this.despawn = Math.min(this.despawn, Math.max(1, t.despawn - 1));
        else if (!this.tame && this.despawn > 0 && --this.despawn === 0) { this.vanish(); return; }
      }
    }
    if (--this.scanCd <= 0) {
      this.scanCd = 10;
      if (this.target === null) this.findTarget(this.traderRef);
    }
    if (this.tryTarget(ctx)) { this.ageTick(ctx); return; }
    // a loose caravan follower trails the llama ahead of it
    const prev = this.caravanPrev;
    if (prev) {
      if (!prev.alive || prev.removed) this.caravanPrev = null;
      else {
        this.ageTick(ctx);
        const dx = prev.x - this.x, dz = prev.z - this.z, d2 = dx * dx + dz * dz;
        if (d2 > 2.6 * 2.6) { if (!this.navTo(ctx, prev.x, prev.y, prev.z, 1.1, 3, true, 24, 200)) this.setMove(dx, dz, 1.1); }
        this.faceToward(prev.x, prev.z);
        this.lookAt(prev.x, prev.y + prev.eyeHeight, prev.z);
        return;
      }
    }
    // a trader llama keeps close to its trader (the lead pulls it back when it strays)
    const tr = this.traderRef;
    if (this.isTrader && tr && tr.alive && !this.baby) {
      const dx = tr.x - this.x, dz = tr.z - this.z, d2 = dx * dx + dz * dz;
      if (d2 > 6 * 6) {
        this.ageTick(ctx);
        if (!this.navTo(ctx, tr.x, tr.y, tr.z, 1.0, 3, true, 24, 200)) this.setMove(dx, dz, 1.0);
        return;
      }
    }
    super.ai(ctx);
  }

  // ------------------------------------------------------------ breeding

  override inherit(a: Animal, b: Animal, rnd: () => number): void {
    const pa = a as Llama, pb = b as Llama;
    if (!(pa instanceof Llama) || !(pb instanceof Llama)) return;
    this.setVariant(rnd() < 0.5 ? pa.variant : pb.variant);
    const avg = (pa.strength + pb.strength) / 2;
    this.setStrength(Math.round(avg + (rnd() < 0.5 ? 0 : rnd() < 0.5 ? -1 : 1)));
    this.setMaxHealth(Math.max(15, Math.min(30, Math.round((pa.maxHealth + pb.maxHealth) / 2 + (rnd() * 4 - 2)))));
  }

  // ----------------------------------------------------------------- drops

  loot(rnd: () => number): Stack[] {
    if (this.noDrops()) return [];
    const out: Stack[] = [];
    const n = roll(rnd, 0, 2);
    if (n > 0) out.push(stackOf(I.LEATHER, n));
    if (this.chest) {
      out.push(stackOf(CHEST_FACING_0, 1));
      for (const s of this.items) if (s.count > 0 && s.id > 0) out.push({ ...s });
    }
    return out;
  }

  // ----------------------------------------------------------------- view

  override syncView(host: EntityHost): void {
    this.color = (this.variant & 3) | (this.carpet << 2) | (this.chest ? 128 : 0);
    super.syncView(host);
  }

  override saveExtra(): unknown {
    const o = super.saveExtra() as Record<string, unknown>;
    o.v = this.variant; o.s = this.strength; o.mh = this.maxHealth;
    if (this.tame) o.t = 1;
    if (this.temper) o.tp = this.temper;
    if (this.carpet) o.cp = this.carpet;
    if (this.chest) { o.ch = 1; o.it = this.items.map((s) => (s.count > 0 && s.id > 0 ? s : null)); }
    if (this.isTrader) {
      o.dp = this.despawn; o.tu = this.traderUid;
      if (leashes.holderOf(this)?.kind === 'mob') o.lt = 1;
    }
    return o;
  }

  override loadExtra(d: unknown): void {
    super.loadExtra(d);
    const o = (d ?? {}) as Record<string, unknown>;
    const n = (v: unknown, def = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : def);
    this.setVariant(n(o.v));
    this.strength = Math.max(1, Math.min(5, Math.floor(n(o.s, this.strength))));
    if (n(o.mh) > 0) this.setMaxHealth(Math.max(1, n(o.mh)));
    this.tame = n(o.t) === 1;
    this.temper = Math.max(0, n(o.tp));
    this.carpet = Math.max(0, Math.min(16, Math.floor(n(o.cp))));
    this.chest = n(o.ch) === 1;
    this.items = [];
    this.resizeChest();
    if (this.chest && Array.isArray(o.it)) {
      (o.it as unknown[]).slice(0, this.items.length).forEach((s, i) => {
        const r = s as Partial<Stack> | null;
        if (r && typeof r.id === 'number' && typeof r.count === 'number' && r.count > 0) this.items[i] = { ...r, id: r.id, count: r.count, dmg: r.dmg ?? 0 };
      });
    }
    if (this.isTrader) {
      this.despawn = Math.max(1, Math.min(TRADER_LLAMA_DESPAWN, n(o.dp, TRADER_LLAMA_DESPAWN)));
      this.traderUid = n(o.tu);
      this.needsLeash = n(o.lt) === 1 && this.traderUid > 0;
    }
  }
}

export const LLAMA_TYPES: readonly MobType[] = ['llama', 'trader_llama'];
